import React from 'react';
import { Grid, Select, MenuItem, TextField, Button, Autocomplete, SelectChangeEvent, FormControl, InputLabel, Switch, FormGroup, FormControlLabel, FormLabel } from '@mui/material';
import { Search, Download } from '@mui/icons-material';
import LoadingButton from '@mui/lab/LoadingButton';

interface SearchFiltersProps {
    selectedPool: string;
    handlePoolChange: (event: SelectChangeEvent<string>) => void;
    searchOptions: SearchOption[];
    searchTerm: number[];
    setSearchTerm: (value: number[]) => void;
    searchMachineOptions: SearchOption[];
    searchMachineTerm: number[];
    setSearchMachineTerm: (value: number[]) => void;
    searchGroupTerm: string[];
    setSearchGroupTerm: (value: string[]) => void;
    searchKeyInput: string;
    setSearchKeyInput: (value: string) => void;
    handleSearch: () => void;
    handleDownloadCSV: () => void;
    totalRecords: number;
    downloadLoader: boolean;
    invoiceType: string,
    setInvoiceType: (value: string) => void;
    billingScheme: string;
    setBillingScheme: (value: string) => void;
    machineType: string;
    setMachineType: (value: string) => void;
    activeDummy: boolean;
    setActiveDummy: (value: boolean) => void;
    setPage: (value: number) => void;

    resellerList: SearchOption[];
    selectedResellerIds: number[];
    setSelectedResellerIds: (value: number[]) => void;
}

interface SearchOption {
    id: number;
    name: string;
}


const SearchFilters: React.FC<SearchFiltersProps> = ({
    selectedPool,
    handlePoolChange,
    searchOptions,
    searchTerm,
    setSearchTerm,
    searchMachineOptions,
    searchMachineTerm,
    setSearchMachineTerm,
    searchGroupTerm,
    setSearchGroupTerm,
    searchKeyInput,
    setSearchKeyInput,
    handleSearch,
    handleDownloadCSV,
    totalRecords,
    downloadLoader,
    invoiceType,
    setInvoiceType,
    billingScheme,
    setBillingScheme,
    machineType,
    setMachineType,
    activeDummy,
    setActiveDummy,
    setPage,

    resellerList,
    selectedResellerIds,
    setSelectedResellerIds
}) => {


    const searchGroupOptions = [
        { id: 'customerId', name: 'Customer ID' },
        // { id: 'user', name: 'User' },
        { id: 'machineModelName', name: 'Machine Model' },
        { id: 'currency', name: 'Currency' },
        // { id: 'expenseInDollar', name: 'Expense in Dollar' },
        { id: 'uptimeInHours', name: 'Uptime in Hours' },
        // { id: 'power', name: 'Power' },
        { id: 'hostingRate', name: 'Hosting Rate' },
        // { id: 'spend', name: 'Spend' },
        { id: 'workerEngine', name: 'Worker Engine' },
        { id: 'expenseDate', name: 'Expense Date' },
        // { id: 'totalCharge', name: 'Total Charge' },
    ];

    const label = { inputProps: { 'aria-label': 'Switch demo' } };

    const handleSwitchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setPage(1)
        setActiveDummy(event.target.checked);
    };

    return (
        <Grid
            container
            spacing={1}
            sx={{
                marginBottom: '10px',
                alignItems: 'center',
                flexWrap: 'nowrap',
                width: '100%',
            }}
        >
            <Grid
                item
                xs={10}
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                }}
            >
                <Select
                    size="small"
                    value={selectedPool}
                    onChange={handlePoolChange}
                    sx={{ flex: 1 }}
                >
                    <MenuItem value="ALL">ALL</MenuItem>
                    <MenuItem value="SEGPOOL">SEGPOOL</MenuItem>
                    <MenuItem value="CLOUD">CLOUD</MenuItem>
                </Select>

                <FormControl size="small" sx={{ flex: 1, minWidth: 160 }} fullWidth>
                    <InputLabel id="invoice-type-label">Invoice Type</InputLabel>
                    <Select
                        labelId="invoice-type-label"
                        value={invoiceType}
                        onChange={(e) => setInvoiceType(e.target.value)}
                        label="Invoice Type"
                    >
                        <MenuItem value="">
                            <em>All</em>
                        </MenuItem>
                        <MenuItem value="HASH_SALE">HASH_SALE</MenuItem>
                        <MenuItem value="MACHINE_SALE">MACHINE_SALE</MenuItem>
                        <MenuItem value="AUTOMATED_MACHINE_SALE">AUTOMATED_MACHINE_SALE</MenuItem>
                    </Select>
                </FormControl>

                <FormControl size="small" sx={{ flex: 1, minWidth: 160 }} fullWidth>
                    <InputLabel id="billing-scheme-label">Billing Scheme</InputLabel>
                    <Select
                        labelId="billing-scheme-label"
                        value={billingScheme}
                        onChange={(e) => setBillingScheme(e.target.value)}
                        label="Billing Scheme"
                    >
                        <MenuItem value="">
                            <em>All</em>
                        </MenuItem>
                        <MenuItem value="MONTHLY">MONTHLY</MenuItem>
                        <MenuItem value="PREPAID">PREPAID</MenuItem>
                        <MenuItem value="AUTO_SETTLEMENT">AUTO_SETTLEMENT</MenuItem>
                    </Select>
                </FormControl>

                <FormControl size="small" sx={{ flex: 1, minWidth: 150 }} fullWidth>
                    <InputLabel id="machine-type-label">Machine Type</InputLabel>
                    <Select
                        labelId="machine-type-label"
                        value={machineType}
                        onChange={(e) => {
                            setPage(1);
                            setMachineType(e.target.value);
                        }}
                        label="Machine Type"
                    >
                        <MenuItem value="">
                            <em>All</em>
                        </MenuItem>
                        <MenuItem value="VIRTUAL">Fractional</MenuItem>
                        <MenuItem value="PHYSICAL">Whole</MenuItem>
                    </Select>
                </FormControl>


                <Autocomplete
                    multiple
                    options={searchOptions}
                    value={searchOptions.filter((option) => searchTerm.includes(option.id))}
                    onChange={(event, newValue) => {
                        setSearchTerm(newValue.map((option) => option.id));
                    }}
                    getOptionLabel={(option) => option.name}
                    isOptionEqualToValue={(option, value) => option.id === value.id}
                    renderInput={(params) => <TextField {...params} label="Customer" size="small" fullWidth />}
                    sx={{ flex: 3 }}
                />

                <Autocomplete
                    multiple
                    options={resellerList}
                    value={resellerList.filter((option) => selectedResellerIds.includes(option.id))}
                    onChange={(event, newValue) => {
                        setSelectedResellerIds(newValue.map((option) => option.id));
                    }}
                    getOptionLabel={(option) => option.name}
                    isOptionEqualToValue={(option, value) => option.id === value.id}
                    renderInput={(params) => <TextField {...params} label="Reseller" size="small" fullWidth />}
                    sx={{ flex: 3 }}
                />

                <Autocomplete
                    multiple
                    options={searchMachineOptions}
                    value={searchMachineOptions.filter((option) => searchMachineTerm.includes(option.id))}
                    onChange={(event, newValue) => {
                        setSearchMachineTerm(newValue.map((option) => option.id));
                    }}
                    getOptionLabel={(option) => option.name}
                    isOptionEqualToValue={(option, value) => option.id === value.id}
                    renderInput={(params) => <TextField {...params} label="Machine Model" size="small" fullWidth />}
                    sx={{ flex: 3 }}
                />

                <Autocomplete
                    multiple
                    options={searchGroupOptions}
                    value={searchGroupOptions.filter((option) => searchGroupTerm.includes(option.id))}
                    onChange={(event, newValue) => {
                        setSearchGroupTerm(newValue.map((option) => option.id));
                    }}
                    getOptionLabel={(option) => option.name}
                    isOptionEqualToValue={(option, value) => option.id === value.id}
                    renderInput={(params) => <TextField {...params} label="Grouped By" size="small" fullWidth />}
                    sx={{ flex: 3 }}
                />


            </Grid>
            <Grid item xs={2} sx={{ display: 'flex', alignItems: 'center' }}>
                <TextField
                    size="small"
                    label="Search Term"
                    fullWidth
                    value={searchKeyInput}
                    onChange={(e) => setSearchKeyInput(e.target.value)}
                    sx={{ width: '100%' }}
                    onKeyPress={(e) => {
                        if (e.key === 'Enter') {
                            handleSearch();
                        }
                    }}
                />
            </Grid>
            <Grid
                item
                xs={2}
                sx={{
                    display: 'flex',
                    justifyContent: 'flex-start',
                    alignItems: 'center',
                    gap: '8px',
                }}
            >
                <Button variant="contained" size="small" sx={{ paddingY: 1 }} onClick={handleSearch}>
                    <Search />
                </Button>
                {downloadLoader ?
                    <LoadingButton loading variant="outlined">
                        Submit
                    </LoadingButton>
                    :
                    <Button
                        variant="contained"
                        size="small"
                        sx={{
                            paddingY: 1,
                            backgroundColor: totalRecords === 0 ? 'grey.300' : 'primary.main',
                            color: totalRecords === 0 ? 'grey.700' : 'white',
                            pointerEvents: totalRecords === 0 ? 'none' : 'auto',
                        }}
                        onClick={handleDownloadCSV}
                        disabled={totalRecords === 0}
                    >
                        <Download />
                    </Button>
                }
                <FormGroup>
                    <FormLabel component="legend" sx={{ fontSize: '0.8rem' }} >Dummy</FormLabel>
                    <FormControlLabel
                        control={
                            <Switch
                                checked={activeDummy}
                                onChange={handleSwitchChange}
                                name="activeDummy"
                            />
                        }
                        label=""
                    />
                </FormGroup>

            </Grid>
        </Grid>
    );
};

export default SearchFilters;
